import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  Button, FormControl, FormLabel, Input, Select, Flex, Text, Alert, AlertIcon,
  Spinner, Box,
} from '@chakra-ui/react';
import * as FiIcons from 'react-icons/fi';
import { createVideo } from '../../../../../store/videos/videosSlice';
import theme from '../../../../global/theme';
import axiosInstance from '../../../../../utilities/axios';

const CATEGORIES = ['عام', 'حروب', 'زراعة', 'نقاشات', 'مراجعات', 'تعليم'];

const CreateModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const videos = useSelector(state => state.videos);
  const token = useSelector(state => state.auth.token);
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: { category: 'عام', is_active: true }
  });

  const [fetchingStats, setFetchingStats] = useState(false);
  const [fetchedStats, setFetchedStats] = useState(null);
  const debounceRef = useRef(null);
  const youtubeUrl = watch('youtube_url');

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFetchedStats(null);
    setValue('views', '0');
    setValue('likes', '0');
    setValue('comments', '0');
    setValue('duration', '0:00');
    if (!youtubeUrl || !youtubeUrl.includes('youtu')) return;
    debounceRef.current = setTimeout(async () => {
      setFetchingStats(true);
      try {
        const res = await axiosInstance.get(`/api/fetch_youtube_stats?url=${encodeURIComponent(youtubeUrl)}`, {
          headers: { Authorization: token }
        });
        if (res.data.success) {
          const { title, creator, thumbnail, creator_avatar, views, likes, comments, duration } = res.data.data;
          if (title) setValue('title', title);
          if (creator) setValue('creator', creator);
          setValue('youtube_thumbnail_url', thumbnail || '');
          setValue('youtube_creator_avatar_url', creator_avatar || '');
          setValue('views', views);
          setValue('likes', likes);
          setValue('comments', comments);
          setValue('duration', duration || '0:00');
          setFetchedStats({ title, creator, thumbnail, creator_avatar, views, likes, comments, duration });
        }
      } catch (_) {
        // silently ignore
      } finally {
        setFetchingStats(false);
      }
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [youtubeUrl, setValue, token]);

  const onSubmit = (values) => {
    const fd = new FormData();
    Object.entries(values).forEach(([k, v]) => {
      if (k === 'thumbnail' && v?.[0]) fd.append('thumbnail', v[0]);
      else if (v !== undefined && v !== null) fd.append(k, v);
    });
    dispatch(createVideo(fd)).unwrap().then(() => onClose());
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius={4} paddingBlock={4} bg={theme.dark}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader textAlign="center" color="orange" fontSize={20}>إضافة فيديو</ModalHeader>
          <ModalBody>
            {videos.errors.length > 0 && (
              <Alert status="error" variant="left-accent" marginBottom={4}>
                <AlertIcon />
                {videos.errors.map((e, i) => <Text key={i}>{e?.msg}</Text>)}
              </Alert>
            )}

            {/* Hidden fields auto-populated from YouTube */}
            <input type="hidden" {...register('views')} />
            <input type="hidden" {...register('likes')} />
            <input type="hidden" {...register('comments')} />
            <input type="hidden" {...register('duration')} />
            <input type="hidden" {...register('youtube_thumbnail_url')} />
            <input type="hidden" {...register('youtube_creator_avatar_url')} />

            <FormControl mb={4}>
              <FormLabel color={theme.light}>عنوان الفيديو *</FormLabel>
              <Input bg={theme.light} border="none" borderRadius={4}
                {...register('title', { required: 'مطلوب' })} />
              {errors.title && <Text color="red.400" mt={1}>{errors.title.message}</Text>}
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color={theme.light}>صانع المحتوى *</FormLabel>
              <Input bg={theme.light} border="none" borderRadius={4}
                {...register('creator', { required: 'مطلوب' })} />
              {errors.creator && <Text color="red.400" mt={1}>{errors.creator.message}</Text>}
            </FormControl>

            <FormControl mb={4}>
              <Flex align="center" gap={2} mb={1}>
                <FormLabel color={theme.light} mb={0}>رابط يوتيوب</FormLabel>
                {fetchingStats && <Spinner size="xs" color="orange.300" />}
              </Flex>
              <Input bg={theme.light} border="none" borderRadius={4} {...register('youtube_url')}
                placeholder="https://www.youtube.com/watch?v=..." />
              {fetchedStats && !fetchingStats && (
                <Box mt={2} p={2} bg="whiteAlpha.100" borderRadius={4} fontSize="sm" color={theme.light}>
                  <Flex gap={3} align="center">
                    {fetchedStats.thumbnail && (
                      <img src={fetchedStats.thumbnail} alt={fetchedStats.title}
                        style={{ width: 96, borderRadius: 4, objectFit: 'cover' }} />
                    )}
                    <Flex direction="column" gap={1}>
                      {fetchedStats.title && <Text fontWeight="bold">{fetchedStats.title}</Text>}
                      {fetchedStats.creator && (
                        <Flex align="center" gap={2}>
                          {fetchedStats.creator_avatar && (
                            <img src={fetchedStats.creator_avatar} alt={fetchedStats.creator}
                              style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                          )}
                          <Text fontSize="xs" color="whiteAlpha.700">{fetchedStats.creator}</Text>
                        </Flex>
                      )}
                      <Flex gap={4} flexWrap="wrap">
                        <Text>👁 {fetchedStats.views}</Text>
                        <Text>👍 {fetchedStats.likes}</Text>
                        <Text>💬 {fetchedStats.comments}</Text>
                        {fetchedStats.duration && <Text>⏱ {fetchedStats.duration}</Text>}
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
              )}
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color={theme.light}>التصنيف</FormLabel>
              <Select bg={theme.light} border="none" borderRadius={4} {...register('category')}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color={theme.light}>صورة مصغرة (اختياري إن تم جلبها من يوتيوب)</FormLabel>
              <Input type="file" accept="image/*" bg={theme.light} border="none" borderRadius={4} p={1}
                {...register('thumbnail')} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={3}>
              <Button type="submit" rightIcon={<FiIcons.FiSave />} colorScheme="green"
                isLoading={videos.isLoading}>حفظ</Button>
              <Button type="button" rightIcon={<FiIcons.FiX />} colorScheme="red"
                isLoading={videos.isLoading} onClick={onClose}>إلغاء</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CreateModal;
